import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import StatsTable, { TableColumn, TableRow, sharedScrollbarStyles } from '../common/StatsTable';
import { LoadingAnimation } from '../common/LoadingAnimation';

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  
  @media (max-width: 30rem) {
    justify-content: center;
  }
`;

const ScrollWrapper = styled.div`
  position: relative;
  max-height: clamp(320px, 45vh, 540px);
  overflow: hidden;
  border-radius: 12px;
  background: var(--color-surface);
`;

const ScrollInner = styled.div`
  overflow: auto;
  max-height: inherit;
  ${sharedScrollbarStyles};

  .stats-table-scroll-content {
    width: max-content;
  }
`;

const ToggleControl = styled.div`
  display: inline-flex;
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: 0.15rem;
`;

const ToggleOption = styled.button<{ $active?: boolean }>`
  padding: 0.35rem 0.85rem;
  border: none;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $active }) => ($active ? 'var(--color-accent)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#ffffff' : 'var(--color-text-secondary)')};

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--color-accent-light)' : 'var(--color-surface)')};
    color: ${({ $active }) => ($active ? '#ffffff' : 'var(--color-text-primary)')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

interface PlayerStatsTableProps {
  statsType: 'traditional' | 'advanced' | 'insights';
  opponentPlayers: any[];
  isLoadingPlayers: boolean;
}

export const PlayerStatsTable: React.FC<PlayerStatsTableProps> = ({
  statsType,
  opponentPlayers,
  isLoadingPlayers
}) => {
  const [activeView, setActiveView] = useState<'averages' | 'totals' | 'advanced'>('averages');
  
  // All hooks must be called before any early returns
  const playersArray = Array.isArray(opponentPlayers) ? opponentPlayers : [];

  // Check which views have data
  const hasAveragesData = useMemo(() => {
    return playersArray.some(player => {
      const stats = player.own || {};
      return stats.average && Object.keys(stats.average).length > 0;
    });
  }, [playersArray]);

  const hasTotalsData = useMemo(() => {
    return playersArray.some(player => {
      const stats = player.own || {};
      return stats.total && Object.keys(stats.total).length > 0;
    });
  }, [playersArray]);

  const hasAdvancedData = useMemo(() => {
    return playersArray.some(player => {
      const stats = player.own || {};
      return stats.advanced && Object.keys(stats.advanced).length > 0;
    });
  }, [playersArray]);

  // Auto-select view if current view has no data
  const currentView = useMemo(() => {
    if (activeView === 'averages' && hasAveragesData) return 'averages';
    if (activeView === 'totals' && hasTotalsData) return 'totals';
    if (activeView === 'advanced' && hasAdvancedData) return 'advanced';
    // Fallback to first available view
    if (hasAveragesData) return 'averages';
    if (hasTotalsData) return 'totals';
    if (hasAdvancedData) return 'advanced';
    return 'averages';
  }, [activeView, hasAveragesData, hasTotalsData, hasAdvancedData]);

  // Utility function to safely format stat values
  const formatStatValue = (value: any): string => {
    if (value === undefined || value === null) {
      return '-'; // Data doesn't exist
    }
    
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) {
      return '-'; // Invalid data
    }
    
    // For percentages (values between 0 and 100), show with 1 decimal place
    if (numValue >= 0 && numValue <= 100) {
      return numValue.toFixed(1);
    }
    
    // For other stats, show as integer if whole number, otherwise 1 decimal
    return numValue % 1 === 0 ? numValue.toString() : numValue.toFixed(1);
  };

  const calculatePercentage = (made: any, attempted: any): string => {
    if (made === undefined || made === null || attempted === undefined || attempted === null) {
      return '-';
    }
    try {
      const madeVal = typeof made === 'number' ? made : parseFloat(made);
      const attemptedVal = typeof attempted === 'number' ? attempted : parseFloat(attempted);
      if (isNaN(madeVal) || isNaN(attemptedVal) || attemptedVal === 0) {
        return '0.0';
      }
      return ((madeVal / attemptedVal) * 100).toFixed(1);
    } catch (e) {
      return '-';
    }
  };

  const getStatValue = (player: any, stats: any, statKeys: string[]): any => {
    for (const key of statKeys) {
      const pathParts = key.split('.');
      let value: any;

      if (pathParts[0] === 'player') {
        value = player;
        pathParts.shift();
      } else {
        value = stats;
      }

      for (const part of pathParts) {
        value = value?.[part];
        if (value === undefined || value === null) break;
      }

      if (value !== undefined && value !== null) {
        return value;
      }
    }

    return undefined;
  };

  type ColumnDef = {
    key: string;
    label: string;
    isFixed?: boolean;
    getter: (player: any, stats: any) => string;
  };

  // Column definitions for averages view
  const averagesColumnDefs: ColumnDef[] = [
    { key: 'player', label: 'PLAYER', isFixed: true, getter: player => player.player_name || 'Unknown' },
    { key: 'split', label: 'SPLIT', getter: player => player.split || 'overall' },
    { key: 'min', label: 'MIN', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.minutes'])) },
    { key: 'pts', label: 'PTS', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.points'])) },
    { key: 'fgm', label: 'FGM', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.field_goals_made'])) },
    { key: 'fga', label: 'FGA', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.field_goals_attempted'])) },
    { key: 'fgPct', label: 'FG%', getter: (player, stats) => calculatePercentage(getStatValue(player, stats, ['average.field_goals_made']), getStatValue(player, stats, ['average.field_goals_attempted'])) },
    { key: 'threePm', label: '3PM', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.three_pointers_made'])) },
    { key: 'threePa', label: '3PA', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.three_pointers_attempted'])) },
    { key: 'threePct', label: '3P%', getter: (player, stats) => calculatePercentage(getStatValue(player, stats, ['average.three_pointers_made']), getStatValue(player, stats, ['average.three_pointers_attempted'])) },
    { key: 'ftm', label: 'FTM', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.free_throws_made'])) },
    { key: 'fta', label: 'FTA', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.free_throws_attempted'])) },
    { key: 'ftPct', label: 'FT%', getter: (player, stats) => calculatePercentage(getStatValue(player, stats, ['average.free_throws_made']), getStatValue(player, stats, ['average.free_throws_attempted'])) },
    { key: 'oreb', label: 'OREB', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.offensive_rebounds'])) },
    { key: 'dreb', label: 'DREB', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.defensive_rebounds'])) },
    { key: 'reb', label: 'REB', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.rebounds'])) },
    { key: 'ast', label: 'AST', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.assists'])) },
    { key: 'tov', label: 'TOV', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.turnovers'])) },
    { key: 'stl', label: 'STL', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.steals'])) },
    { key: 'blk', label: 'BLK', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.blocks'])) },
    { key: 'pf', label: 'PF', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.personal_fouls'])) },
    // { key: 'plusMinus', label: '+/-', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['average.plus_minus'])) }
  ];

  // Column definitions for totals view
  const totalsColumnDefs: ColumnDef[] = [
    { key: 'player', label: 'PLAYER', isFixed: true, getter: player => player.player_name || 'Unknown' },
    { key: 'split', label: 'SPLIT', getter: player => player.split || 'overall' },
    { key: 'min', label: 'MIN', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.minutes'])) },
    { key: 'pts', label: 'PTS', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.points'])) },
    { key: 'fgm', label: 'FGM', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.field_goals_made'])) },
    { key: 'fga', label: 'FGA', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.field_goals_attempted'])) },
    { key: 'fgPct', label: 'FG%', getter: (player, stats) => calculatePercentage(getStatValue(player, stats, ['total.field_goals_made']), getStatValue(player, stats, ['total.field_goals_attempted'])) },
    { key: 'threePm', label: '3PM', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.three_pointers_made'])) },
    { key: 'threePa', label: '3PA', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.three_pointers_attempted'])) },
    { key: 'threePct', label: '3P%', getter: (player, stats) => calculatePercentage(getStatValue(player, stats, ['total.three_pointers_made']), getStatValue(player, stats, ['total.three_pointers_attempted'])) },
    { key: 'ftm', label: 'FTM', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.free_throws_made'])) },
    { key: 'fta', label: 'FTA', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.free_throws_attempted'])) },
    { key: 'ftPct', label: 'FT%', getter: (player, stats) => calculatePercentage(getStatValue(player, stats, ['total.free_throws_made']), getStatValue(player, stats, ['total.free_throws_attempted'])) },
    { key: 'oreb', label: 'OREB', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.offensive_rebounds'])) },
    { key: 'dreb', label: 'DREB', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.defensive_rebounds'])) },
    { key: 'reb', label: 'REB', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.rebounds'])) },
    { key: 'ast', label: 'AST', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.assists'])) },
    { key: 'tov', label: 'TOV', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.turnovers'])) },
    { key: 'stl', label: 'STL', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.steals'])) },
    { key: 'blk', label: 'BLK', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.blocks'])) },
    { key: 'pf', label: 'PF', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.personal_fouls'])) },
    { key: 'plusMinus', label: '+/-', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.plus_minus'])) }
  ];

  // Column definitions for advanced view
  const advancedColumnDefs: ColumnDef[] = [
    { key: 'player', label: 'PLAYER', isFixed: true, getter: player => player.player_name || 'Unknown' },
    { key: 'split', label: 'SPLIT', getter: player => player.split || 'overall' },
    { key: 'min', label: 'MIN', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['total.minutes'])) },
    { key: 'offRtg', label: 'OFF RTG', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.offensive_rating'])) },
    { key: 'defRtg', label: 'DEF RTG', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.defensive_rating'])) },
    { key: 'netRtg', label: 'NET RTG', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.net_rating'])) },
    { key: 'astPct', label: 'AST%', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.assist_percentage'])) },
    { key: 'astTo', label: 'AST/TO', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.assist_turnover_ratio'])) },
    { key: 'astRatio', label: 'AST RATIO', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.assist_percentage'])) },
    { key: 'orebPct', label: 'OREB%', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.offensive_rebound_pct'])) },
    { key: 'drebPct', label: 'DREB%', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.defensive_rebound_pct'])) },
    { key: 'rebPct', label: 'REB%', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.total_rebound_pct'])) },
    { key: 'toRatio', label: 'TO RATIO', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.turnover_percentage'])) },
    { key: 'efgPct', label: 'EFG%', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['shooting.effective_fg_pct'])) },
    { key: 'tsPct', label: 'TS%', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['shooting.true_shooting_pct'])) },
    { key: 'usgPct', label: 'USG%', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.usage_rate'])) },
    { key: 'pace', label: 'PACE', getter: (player, stats) => formatStatValue(getStatValue(player, stats, ['advanced.pace'])) },
    { key: 'pie', label: 'PIE', getter: () => '-' }
  ];

  // Select column definitions based on active view
  const columnDefs = useMemo(() => {
    if (currentView === 'averages') return averagesColumnDefs;
    if (currentView === 'totals') return totalsColumnDefs;
    return advancedColumnDefs;
  }, [currentView]);

  const columns: TableColumn[] = useMemo(() => {
    return columnDefs.map(({ key, label, isFixed }) => ({
    key,
    label,
    isFixed
  }));
  }, [columnDefs]);

  const data: TableRow[] = useMemo(() => {
    return playersArray.map((player) => {
    const stats = player.own || {};
    const row: TableRow = {};

    columnDefs.forEach(({ key, getter }) => {
      row[key] = getter(player, stats);
    });

    return row;
  });
  }, [playersArray, columnDefs]);

  // Early returns after all hooks
  if (statsType === 'insights') {
    return (
      <StatsTable
        columns={[{ key: 'insights', label: 'INSIGHTS', isFixed: true }]}
        data={[{ insights: 'Insights are displayed in the main content area above' }]}
        noDataMessage="Insights are displayed in the main content area above"
        stickyLeadingColumnCount={1}
        isReady={!isLoadingPlayers}
        loading={isLoadingPlayers}
        loadingMessage="Loading players..."
      />
    );
  }

  if (isLoadingPlayers) {
    return <LoadingAnimation text="Loading player table" dotInterval={1000} />;
  }

  if (!opponentPlayers || opponentPlayers.length === 0) {
    return (
      <StatsTable
        columns={[{ key: 'noData', label: 'No Data' }]}
        data={[{ noData: 'No players found for this team/season' }]}
        noDataMessage="No players found for this team/season"
        stickyLeadingColumnCount={1}
        isReady={!isLoadingPlayers}
      />
    );
  }

  const hasPlayers = playersArray.length > 0;

  if (isLoadingPlayers) {
    return <LoadingAnimation text="Loading player table" dotInterval={1000} />;
  }

  if (!hasPlayers) {
    return (
      <StatsTable
        columns={[{ key: 'noData', label: 'No Data' }]}
        data={[{ noData: 'No players found for this team/season' }]}
        noDataMessage="No players found for this team/season"
        stickyLeadingColumnCount={1}
        isReady={!isLoadingPlayers}
      />
    );
  }

  return (
    <TableSection>
      <ToggleRow>
        <ToggleControl>
          <ToggleOption
            type="button"
            onClick={() => setActiveView('averages')}
            $active={currentView === 'averages'}
            disabled={!hasAveragesData}
          >
            Averages
          </ToggleOption>
          {/* <ToggleOption
            type="button"
            onClick={() => setActiveView('totals')}
            $active={currentView === 'totals'}
            disabled={!hasTotalsData}
          >
            Totals
          </ToggleOption> */}
          {/* Advanced tab hidden for PlayerStatsTable - only available for TeamStatsTable */}
        </ToggleControl>
      </ToggleRow>

      <ScrollWrapper>
        <ScrollInner>
          <div className="stats-table-scroll-content">
    <StatsTable
      columns={columns}
              data={data}
      loading={isLoadingPlayers}
      loadingMessage="Loading players..."
      noDataMessage="No players found for this team/season"
              stickyFirstColumn
      stickyLeadingColumnCount={1}
      isReady={!isLoadingPlayers}
              disableContainerScroll
    />
          </div>
        </ScrollInner>
      </ScrollWrapper>
    </TableSection>
  );
};
